import React, { useState } from 'react';
import './GeoReportForm.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

export type CompanyScaleOption = 'auto' | 'solo' | 'small' | 'mid' | 'enterprise';

interface GeoReportFormProps {
  isGenerating: boolean;
  onGenerate: (params: { url: string; companyScale: CompanyScaleOption }) => void;
  usage: { used: number; limit: number };
}

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(normalizeUrl(value));
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.includes('.');
  } catch {
    return false;
  }
};

const GeoReportForm: React.FC<GeoReportFormProps> = ({ isGenerating, onGenerate, usage }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [companyScale, setCompanyScale] = useState<CompanyScaleOption>('auto');
  const [touched, setTouched] = useState(false);

  const isLimitReached = usage.used >= usage.limit;
  const isUrlInvalid = touched && !isValidUrl(url);
  const canSubmit = !isGenerating && !isLimitReached && isValidUrl(url);

  const companyScaleOptions = [
    { label: t('geoReport.companySize.auto'), value: 'auto' as CompanyScaleOption },
    { label: t('geoReport.companySize.solo'), value: 'solo' as CompanyScaleOption },
    { label: t('geoReport.companySize.small'), value: 'small' as CompanyScaleOption },
    { label: t('geoReport.companySize.mid'), value: 'mid' as CompanyScaleOption },
    { label: t('geoReport.companySize.enterprise'), value: 'enterprise' as CompanyScaleOption },
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onGenerate({ url: normalizeUrl(url), companyScale });
    setUrl('');
    setCompanyScale('auto');
    setTouched(false);
  };

  return (
    <div className="geo-report-form">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('geoReport.formTitle')}
        </div>
        <div className="page-box__source-filter">
          <span className={`geo-report-form__usage${isLimitReached ? ' geo-report-form__usage--limit' : ''}`}>
            {t('geoReport.usageCounter', { used: usage.used, limit: usage.limit })}
          </span>
        </div>
      </div>

      <form className="geo-report-form__body" onSubmit={handleSubmit} noValidate>
        <div className="geo-report-form__field">
          <InputText
            className={`geo-report-form__input${isUrlInvalid ? ' form__invalid' : ''}`}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => { if (url.trim()) setTouched(true); }}
            placeholder={t('geoReport.urlPlaceholder')}
            disabled={isGenerating || isLimitReached}
          />
          {isUrlInvalid && <span>{t('geoReport.urlInvalid')}</span>}
        </div>

        <div className="geo-report-form__field geo-report-form__field--company-size">
          <Dropdown
            className="dropdown geo-report-form__company-size"
            value={companyScale}
            options={companyScaleOptions}
            onChange={(e) => setCompanyScale(e.value as CompanyScaleOption)}
            disabled={isGenerating || isLimitReached}
            optionLabel="label"
            placeholder={t('geoReport.companySize.auto')}
          />
        </div>

        <Button
          type="submit"
          className={`btn btn--sm${!canSubmit ? ' btn--disabled' : ''}`}
          disabled={!canSubmit}
          loading={isGenerating}
        >
          {isGenerating ? t('geoReport.generating') : t('geoReport.generateBtn')}
        </Button>
      </form>

      <div className={`form__info geo-report-form__info${!isGenerating ? ' geo-report-form__info--hidden' : ''}`}>
        {t('geoReport.generatingInfo')}
      </div>
    </div>
  );
};

export { GeoReportForm };
export default GeoReportForm;